import { Brain, BookOpen, Clapperboard, ArrowUpRight, PlayCircle } from "lucide-react";
import { FadeUp, Label, Emblem } from "@/components/Primitives";
import { SOCIALS, YOUTUBE_URL } from "@/data/content";
import { SOCIAL_ICONS } from "@/components/SocialIcons";
import CrimsonReveal from "@/components/CrimsonReveal";
import ComingSoon from "@/components/ComingSoon";

const NEXT = [
  {
    Icon: Brain,
    title: "Weekly psychology insights",
    desc: "Receive exclusive psychology lessons that are not posted on social media.",
  },
  {
    Icon: BookOpen,
    title: "Future dossiers",
    desc: "Get early access to new Noir Psyche guides and premium workbooks.",
  },
  {
    Icon: Clapperboard,
    title: "Join the community",
    desc: "Follow Noir Psyche on YouTube, TikTok and Instagram for daily psychology content.",
  },
];

// Large buttons for the three headline platforms, pulled from the shared source.
const BIG_SOCIALS = ["youtube", "instagram", "tiktok"]
  .map((k) => SOCIALS.find((s) => s.key === k))
  .filter(Boolean);

export default function ThankYou() {
  return (
    <main data-testid="thank-you-page" className="relative pt-[72px]">
      <CrimsonReveal />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="crimson-glow absolute -top-10 left-1/2 -translate-x-1/2 h-[520px] w-[720px] rounded-full opacity-50" />
        <div className="crimson-glow absolute bottom-0 right-[-8%] h-[360px] w-[360px] rounded-full opacity-40" />

        <div className="relative mx-auto max-w-[1000px] px-6 md:px-10 py-20 md:py-32 flex flex-col items-center text-center">
          <FadeUp>
            <div className="flex items-center justify-center gap-4 mb-8">
              <span className="h-px w-10" style={{ background: "var(--noir-crimson)" }} />
              <Label>Access Granted</Label>
              <span className="h-px w-10" style={{ background: "var(--noir-crimson)" }} />
            </div>
          </FadeUp>

          <FadeUp delay={0.05}>
            <h1
              data-testid="thank-you-headline"
              className="font-display font-light leading-[0.9] tracking-tight text-4xl md:text-6xl lg:text-7xl"
            >
              Your Dossier Has <br />
              Been <span className="text-crimson italic">Unlocked.</span>
            </h1>
          </FadeUp>

          <FadeUp delay={0.1}>
            <p className="mt-8 max-w-2xl font-display italic text-xl md:text-2xl text-white/80">
              The Psychology They Never Taught You is now being delivered to your
              inbox.
            </p>
          </FadeUp>

          <FadeUp delay={0.15}>
            <p className="mt-8 max-w-xl text-white/55 text-sm md:text-base leading-relaxed">
              Your free guide should arrive within the next few minutes. If you
              don't see it, please check your Promotions, Spam, or Junk folder.
              Make sure to move the email into your Primary inbox so you never miss
              future psychology guides.
            </p>
          </FadeUp>

          <FadeUp delay={0.2}>
            <a
              data-testid="watch-film-cta"
              href={YOUTUBE_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Visit Noir Psyche on YouTube to watch the latest film"
              className="group mt-12 inline-flex items-center gap-3 px-8 py-4 text-white font-mono-accent text-[11px] uppercase tracking-[0.25em] transition-transform active:scale-[0.98]"
              style={{ background: "var(--noir-crimson)" }}
            >
              <PlayCircle size={18} />
              Watch the Latest Film
              <ArrowUpRight size={15} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </FadeUp>
        </div>
      </section>

      {/* What's Next */}
      <section
        data-testid="whats-next"
        className="relative border-t border-white/8 bg-[#080808]"
      >
        <div className="mx-auto max-w-[1500px] px-6 md:px-10 py-24 md:py-32">
          <FadeUp className="text-center mb-16">
            <Label>The Investigation Continues</Label>
            <h2 className="mt-4 font-display text-4xl md:text-6xl tracking-tight">
              What's <span className="text-crimson italic">Next?</span>
            </h2>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {NEXT.map((item, i) => (
              <FadeUp key={item.title} delay={i * 0.08}>
                <div
                  data-testid={`next-card-${i + 1}`}
                  className="group h-full border border-white/8 bg-[#0b0b0b] p-8 md:p-10 transition-colors hover:border-[var(--noir-crimson)]/50"
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-full border border-white/10 bg-white/[0.02] transition-colors group-hover:border-[var(--noir-crimson)]">
                    <item.Icon size={24} className="text-crimson" />
                  </div>
                  <h3 className="mt-8 font-display text-2xl md:text-3xl tracking-tight">
                    {item.title}
                  </h3>
                  <p className="mt-4 text-white/50 text-sm md:text-base leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>

          {/* Social buttons */}
          <FadeUp delay={0.1}>
            <div className="mt-16 flex flex-col sm:flex-row items-stretch justify-center gap-4">
              {BIG_SOCIALS.map((s) => {
                const Icon = SOCIAL_ICONS[s.key];
                return (
                  <a
                    key={s.key}
                    data-testid={`thankyou-social-${s.key}`}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Visit Noir Psyche on ${s.label}`}
                    className="group flex flex-1 sm:flex-none items-center justify-center gap-3 px-10 py-5 border border-white/12 text-white font-mono-accent text-[11px] uppercase tracking-[0.25em] transition-all duration-300 hover:-translate-y-0.5 hover:border-[var(--noir-crimson)] hover:bg-white/[0.03]"
                  >
                    <Icon size={20} className="text-white/70 transition-all duration-300 group-hover:scale-110 group-hover:text-crimson" />
                    {s.label}
                  </a>
                );
              })}
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Quote */}
      <section className="relative">
        <div className="mx-auto max-w-[1000px] px-6 md:px-10 py-24 md:py-32 text-center">
          <FadeUp>
            <Emblem size={30} className="mx-auto text-white/40 mb-10" />
            <blockquote
              data-testid="thank-you-quote"
              className="font-display italic font-light text-3xl md:text-5xl lg:text-6xl leading-tight tracking-tight text-white/90"
            >
              "The first step toward understanding the mind is learning to{" "}
              <span className="text-crimson">observe</span> it."
            </blockquote>
          </FadeUp>
        </div>
      </section>

      {/* Dossier #002 teaser */}
      <ComingSoon />

      {/* Thank-you footer line */}
      <section className="relative border-t border-white/8 bg-[#080808]">
        <div className="mx-auto max-w-[1000px] px-6 md:px-10 py-16 md:py-20 text-center">
          <FadeUp>
            <p className="font-display text-2xl md:text-3xl tracking-tight">
              Thank you for joining Noir Psyche.
            </p>
            <p className="mt-3 font-mono-accent text-[11px] uppercase tracking-[0.3em] text-crimson">
              The investigation has only begun.
            </p>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
